import React, { useState, useEffect, useRef } from 'react';
import { SearchableDropdown, Button, SideBarModal } from '@/components';
import { StepIndicator } from '@/components/StepIndicator';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { Issue, MaintenanceSeverity } from '@/types/common';
import {
  spawnMaintenanceFromIssue,
  SpawnMaintenanceRequest,
  getIssueById,
} from '@/services/api/issues';
import { v4 as uuidv4 } from 'uuid';
import { getBucketUrlImageV2, uploadFileImage, getUserEmail } from '@/utils';
import { extractApiErrorMessage } from '@/utils';
import { updateMaintenance } from '@/services/api/maintenances';

interface CreateMaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  issue: Issue | null;
  onSuccess: (maintenanceCode?: string) => void;
}

interface MaintenanceFormData {
  issueID: string;
  reason: string;
  plateNumber: string;
  maintenanceDetails: string[]; // Changed to string[]
  description: string;
  maintenanceDate: string;
  severity: MaintenanceSeverity;
  estimatedCost: number;
  costBearer: string;
  mitraName?: string;
  clientName?: string;
  vehicleOemName?: string;
  oemServiceCenter: string;
  expectedCompletionDate: string;
  photos: File[];
  imageURL?: string;
  notes: string;
}

const maintenanceCategories = [
  { value: 'BODY_REPAIR', label: 'Body Repair' },
  { value: 'ELECTRICAL_SYSTEM', label: 'Electrical System' },
  { value: 'BATTERY_REPLACEMENT', label: 'Battery Replacement' },
  { value: 'MOTOR_BLDC_REPAIR', label: 'Motor/BLDC Repair' },
  { value: 'TIRE_CHANGE', label: 'Tire Change' },
  { value: 'BRAKE_SYSTEM', label: 'Brake System' },
  { value: 'FRAME_DAMAGE', label: 'Frame Damage' },
  { value: 'GENERAL_MAINTENANCE', label: 'General Maintenance' },
  { value: 'OTHER', label: 'Other' },
];

const severityOptions = [
  { value: MaintenanceSeverity.LOW, label: 'Low' },
  { value: MaintenanceSeverity.MEDIUM, label: 'Medium' },
  { value: MaintenanceSeverity.HIGH, label: 'High' },
  { value: MaintenanceSeverity.CRITICAL, label: 'Critical' },
];

const costBearerOptions = [
  { value: 'MITRA', label: 'Mitra' },
  { value: 'CLIENT', label: 'Client' },
  { value: 'OEM', label: 'OEM' },
  { value: 'DASH_ELECTRIC', label: 'Dash' },
];

export const CreateMaintenanceModal: React.FC<CreateMaintenanceModalProps> = ({
  isOpen,
  onClose,
  issue,
  onSuccess,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(1);

  const [isSaving, setIsSaving] = useState(false);
  const [fetchedVehicleID, setFetchedVehicleID] = useState<string>('');
  const [isResolvingVehicle, setIsResolvingVehicle] = useState(false);

  // Initial State
  const [formData, setFormData] = useState<MaintenanceFormData>({
    issueID: '',
    reason: 'LINKED_TO_INCIDENT',
    plateNumber: '',
    maintenanceDetails: [],
    description: '',
    maintenanceDate: '',
    severity: MaintenanceSeverity.LOW,
    estimatedCost: 0,
    costBearer: '',
    oemServiceCenter: '',
    expectedCompletionDate: '',
    photos: [],
    notes: '',
  });

  // Populate data from Issue when modal opens
  useEffect(() => {
    if (isOpen && issue) {
      const code = issue.mitraCode || issue.driverCode || '';
      const name = issue.mitraName || issue.driverName || '';
      const mitraLabel =
        code && name ? `${code} - ${name}` : name || code || '';

      setFormData({
        issueID: issue.code || issue.ID,
        reason: 'LINKED_TO_INCIDENT',
        plateNumber: issue.vehiclePlatNomor || '',
        maintenanceDetails: [],
        description: '',
        maintenanceDate: new Date().toISOString().split('T')[0],
        severity: issue.severity as unknown as MaintenanceSeverity,
        estimatedCost: issue.estimatedCost || 0,
        costBearer: issue.costBearer || (issue as any).costResponsibility || '',
        mitraName: mitraLabel,
        clientName: issue.clientName,
        vehicleOemName: issue.vehicleOemName,
        oemServiceCenter: '',
        expectedCompletionDate: '',
        photos: [],
        notes: issue.notes || '',
      });

      // Resolve Vehicle ID
      const resolveVehicleID = async () => {
        setIsResolvingVehicle(true);
        if (issue.vehicleID) {
          setFetchedVehicleID(issue.vehicleID);
        } else {
          try {
            const detail: any = await getIssueById(issue.ID);
            // Handle potential nested data structure (e.g. { data: Issue })
            const actualDetail = detail.data || detail;

            if (actualDetail && actualDetail.vehicleID) {
              setFetchedVehicleID(actualDetail.vehicleID);
            } else {
              // Fallback: Try to find vehicleID from other properties if available
              console.warn('Vehicle ID empty in detail:', actualDetail);
              setFetchedVehicleID('');
            }
          } catch (e) {
            console.error('Failed to fetch issue detail', e);
            setFetchedVehicleID('');
          }
        }
        setIsResolvingVehicle(false);
      };
      resolveVehicleID();

      setCurrentStep(1);
    }
  }, [isOpen, issue]);

  const handleClose = () => {
    onClose();
  };

  const handleNext = () => {
    // Validation Step 1
    if (
      formData.maintenanceDetails.length === 0 ||
      !formData.description ||
      !formData.maintenanceDate ||
      !formData.oemServiceCenter ||
      !formData.expectedCompletionDate
    ) {
      showErrorToast(
        'Validation Error',
        'Please fill all required fields (*) in Step 1'
      );
      return;
    }
    setCurrentStep(2);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 0);
  };

  const handleBack = () => {
    setCurrentStep(1);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 0);
  };

  const handleSave = async () => {
    if (!issue) return;

    if (!fetchedVehicleID) {
      showErrorToast('Error', 'Missing Vehicle ID. Cannot create maintenance.');
      return;
    }

    setIsSaving(true);
    try {
      let imageURL = '';

      // Handle Image Upload
      if (formData.photos && formData.photos.length > 0) {
        const file = formData.photos[0];
        const uuid = uuidv4();

        // 1. Get Bucket URL
        const bucketRes = await getBucketUrlImageV2({
          uuid,
          type: 'maintenance', // Maintenance type
        });
        const url = bucketRes.data;

        // 2. Convert to Base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
        });
        const base64Data = base64.split(',')[1];

        // 3. Upload File
        const uploadRes = await uploadFileImage(url, base64Data, file.name);

        if (uploadRes.status === 200) {
          imageURL = url;
        } else {
          throw new Error('Image upload failed');
        }
      } else if (issue.imageURL) {
      }

      const userEmail = getUserEmail();

      const payload: SpawnMaintenanceRequest = {
        categories: formData.maintenanceDetails,
        issueDescription: formData.description,
        estimatedCost: formData.estimatedCost,
        costResponsibility: formData.costBearer,
        oemServiceCenter: formData.oemServiceCenter,
        maintenanceDate: formData.maintenanceDate,
        expectedCompletionDate: formData.expectedCompletionDate,
        createdBy: userEmail,
        vehicleImageURL: imageURL,
        notes: formData.notes,
        severity: formData.severity,
      };

      const result = await spawnMaintenanceFromIssue(issue.ID, payload);

      // Workaround: Backend ignores estimatedCost if it's 0 during spawn, so we force update it
      if (formData.estimatedCost === 0) {
        const maintenanceID =
          result?.data?.maintenanceID ||
          result?.data?.ID ||
          result?.maintenanceID;

        if (maintenanceID) {
          try {
            await updateMaintenance(maintenanceID, {
              estimatedCost: 0,
              updatedBy: getUserEmail(),
            });
          } catch (updateError) {
            showErrorToast(
              'Warning',
              'Maintenance created but failed to set cost to 0. Please update manually.'
            );
          }
        } else {
          showErrorToast(
            'Warning',
            'Maintenance created but cost might not be 0. Please verify.'
          );
        }
      }

      const maintenanceCode = result?.data?.maintenanceCode || 'Maintenance';
      const message = `#${maintenanceCode} created and linked to #${issue.code}`;

      showSuccessToast('Success', message);
      onSuccess(result?.data?.maintenanceCode);
      onClose();
    } catch (error) {
      const msg = extractApiErrorMessage(error, 'Failed to create maintenance');
      showErrorToast('Error', msg);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // Helper for cost bearer display
  const costBearerUpper = formData.costBearer?.toUpperCase();

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={handleClose}
        position="right"
        width="sm"
        contentRef={scrollRef}
        header={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Create Maintenance
            </h2>
            <p className="text-sm font-normal text-gray-600">
              New Maintenance details.
            </p>
            <div className="flex justify-center pb-2 mt-3">
              <StepIndicator
                steps={[
                  { id: 1, label: 'General Data' },
                  { id: 2, label: 'Details' },
                ]}
                currentStep={currentStep}
              />
            </div>
          </div>
        }
        footer={
          currentStep === 1 ? (
            <Button
              variant="primary"
              onClick={handleNext}
              className="w-full py-3"
              disabled={
                formData.maintenanceDetails.length === 0 ||
                !formData.description ||
                !formData.maintenanceDate ||
                !formData.oemServiceCenter ||
                !formData.expectedCompletionDate ||
                isResolvingVehicle ||
                fetchedVehicleID === ''
              }
            >
              {isResolvingVehicle ? 'Loading...' : 'Next'}
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="neutral"
                onClick={handleBack}
                className="flex-1 py-3"
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={
                  isSaving || isResolvingVehicle || fetchedVehicleID === ''
                }
                className="flex-1 py-3"
              >
                {isSaving ? 'Saving...' : 'Create Maintenance'}
              </Button>
            </div>
          )
        }
      >
        <div className="flex-1">
          <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 border-b border-gray-200 heading-field">
            {currentStep === 1 ? 'General Data' : 'Details'}
          </div>

          {!isResolvingVehicle && fetchedVehicleID === '' && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              <i className="ri-error-warning-line mr-2 align-middle"></i>
              Could not resolve Vehicle ID. You cannot proceed with creating
              maintenance for this issue.
            </div>
          )}

          <div className="p-6">
            {currentStep === 1 && (
              <div className="space-y-4">
                {/* IssuesID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IssuesID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 bg-[#F7F7F7] rounded-md px-3 py-2 text-sm text-[#D1D1D1] cursor-not-allowed"
                    value={formData.issueID}
                    disabled
                    readOnly
                  />
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 bg-[#F7F7F7] rounded-md px-3 py-2 text-sm text-[#D1D1D1] cursor-not-allowed"
                    value={formData.reason}
                    disabled
                    readOnly
                  />
                </div>

                {/* Plate Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plate Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 bg-[#F7F7F7] rounded-md px-3 py-2 text-sm text-[#D1D1D1] cursor-not-allowed"
                    value={formData.plateNumber}
                    disabled
                    readOnly
                  />
                </div>

                {/* Maintenance Details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maintenance Details <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={maintenanceCategories}
                    value={formData.maintenanceDetails}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        maintenanceDetails: value as string[],
                      }))
                    }
                    placeholder="Select Maintenance Details..."
                    multiple={true}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                      rows={3}
                      maxLength={200}
                      placeholder="e.g. BLDC rusak karena rider menembus banjir."
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                    />
                    <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                      {formData.description.length}/200 <i className=""></i>
                    </div>
                  </div>
                </div>

                {/* Tanggal Maintenance */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tanggal Maintenance <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 text-gray-900"
                    value={formData.maintenanceDate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        maintenanceDate: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Severity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Severity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 bg-[#F7F7F7] rounded-md px-3 py-2 text-sm text-[#D1D1D1] cursor-not-allowed"
                    value={
                      severityOptions.find(
                        (opt) => opt.value === formData.severity
                      )?.label || formData.severity
                    }
                    disabled
                    readOnly
                  />
                </div>

                {/* Estimated Cost */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Cost <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">Rp</span>
                    </div>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-md pl-10 pr-24 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                      value={formData.estimatedCost}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          estimatedCost: Number(e.target.value),
                        }))
                      }
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                      <img
                        src="https://flagcdn.com/w40/id.png"
                        alt="IDR"
                        className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                      />
                      <span className="text-gray-500 text-sm">IDR</span>
                    </div>
                  </div>
                </div>

                {/* Biaya Ditanggung */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biaya Ditanggung <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 bg-[#F7F7F7] rounded-md px-3 py-2 text-sm text-[#D1D1D1] cursor-not-allowed"
                    value={
                      costBearerOptions.find(
                        (opt) => opt.value === formData.costBearer
                      )?.label || formData.costBearer
                    }
                    disabled
                    readOnly
                  />
                </div>

                {/* Dynamic Cost Bearer Name */}
                {costBearerUpper === 'MITRA' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mitra Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-200 bg-[#F7F7F7] rounded-md px-3 py-2 text-sm text-[#D1D1D1] cursor-not-allowed"
                      value={formData.mitraName || '-'}
                      disabled
                      readOnly
                    />
                  </div>
                )}
                {costBearerUpper === 'CLIENT' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-200 bg-[#F7F7F7] rounded-md px-3 py-2 text-sm text-[#D1D1D1] cursor-not-allowed"
                      value={formData.clientName || '-'}
                      disabled
                      readOnly
                    />
                  </div>
                )}
                {costBearerUpper === 'OEM' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OEM Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-200 bg-[#F7F7F7] rounded-md px-3 py-2 text-sm text-[#D1D1D1] cursor-not-allowed"
                      value={formData.vehicleOemName || '-'}
                      disabled
                      readOnly
                    />
                  </div>
                )}

                {/* OEM Service Center */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OEM Service Center <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                    placeholder="e.g. Electrum - Pondok Indah"
                    value={formData.oemServiceCenter}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        oemServiceCenter: e.target.value,
                      }))
                    }
                  />
                </div>

                {/* Expected Completion Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expected Completion Date{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 text-gray-900"
                    value={formData.expectedCompletionDate}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        expectedCompletionDate: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Foto
                  </label>
                  <div className="space-y-3">
                    {issue?.imageURL ? (
                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-xl bg-[#F7F7F7]">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 flex-shrink-0 relative opacity-60">
                            <svg
                              viewBox="0 0 40 40"
                              fill="none"
                              className="w-full h-full"
                            >
                              <path
                                d="M12 4H24L32 12V34C32 35.1046 31.1046 36 30 36H12C10.8954 36 10 35.1046 10 34V6C10 4.89543 10.8954 4 12 4Z"
                                fill="white"
                                stroke="#9CA3AF"
                                strokeWidth="2"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M24 4V10C24 11.1046 24.8954 12 26 12H32"
                                stroke="#9CA3AF"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <rect
                                x="6"
                                y="20"
                                width="28"
                                height="13"
                                rx="4"
                                fill="#6B7280"
                              />
                              <text
                                x="20"
                                y="29.5"
                                textAnchor="middle"
                                fill="white"
                                fontSize="9"
                                fontWeight="bold"
                                fontFamily="sans-serif"
                              >
                                JPG
                              </text>
                            </svg>
                          </div>
                          <div>
                            <p className="text-base font-medium text-gray-400 truncate max-w-[200px]">
                              {issue.vehiclePlatNomor || 'Image'}.jpg
                            </p>
                            <div className="flex items-center gap-2 text-sm text-gray-400 mt-0.5">
                              <span>120 KB of 120 KB</span>
                              <span>•</span>
                              <span className="flex items-center gap-1 font-medium text-green-600 opacity-70">
                                <i className="ri-checkbox-circle-fill"></i>{' '}
                                Completed
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-200 bg-[#F7F7F7] rounded-lg p-8 text-center cursor-not-allowed select-none">
                        <i className="ri-image-line text-3xl text-[#D1D1D1] mb-2"></i>
                        <p className="text-sm text-[#D1D1D1] font-medium">
                          No photo attached
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <div className="relative">
                    <textarea
                      className="w-full border border-gray-200 bg-[#F7F7F7] rounded-md px-3 py-2 text-sm text-[#D1D1D1] resize-none cursor-not-allowed"
                      rows={4}
                      value={formData.notes}
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </SideBarModal>
    </>
  );
};
